import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Activity, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRequireAuth } from './auth/AutoLoginGate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRICE_BY_COUNTRY = {
  UK: '£4.99/mo',
  IN: '₹499/mo',
  CA: 'C$8.99/mo',
};

/**
 * BuyerPulseSubscribeCard — invites buyers to subscribe to the weekly
 * Pulse-Top-10 email digest. Lives on landing/buyer pages, NOT in
 * dashboard (per product spec — value comes via email cadence).
 */
const BuyerPulseSubscribeCard = ({ country = 'UK', preferredCity = '', dataTestId = 'buyer-pulse-card' }) => {
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [authed, setAuthed] = useState(false);
  const { requireAuth } = useRequireAuth();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setAuthed(true);
    axios.get(`${API_URL}/api/pulse/buyer/subscription`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setSub(r.data)).catch(() => setSub(null));
  }, []);

  const doCheckout = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/pulse/buyer/subscribe`, {
        origin_url: window.location.origin,
        country,
        preferred_country: country,
        preferred_city: preferredCity || null,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.data?.already_active) {
        toast.success(`Already subscribed — expires ${new Date(r.data.expires_at).toLocaleDateString()}`);
      } else if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    // Auto-sense: if guest, the AutoLoginGate modal pops with all 3 sign-in
    // options (Google · WhatsApp · Email) and re-fires checkout after auth.
    // Guest's intent (this subscribe action) is preserved across login.
    requireAuth(() => doCheckout(), { redirectTo: '/pulse', intent: { action: 'pulse_subscribe', country } });
  };

  const isActive = sub?.status === 'active';
  const price = PRICE_BY_COUNTRY[country] || PRICE_BY_COUNTRY.UK;

  return (
    <div
      data-testid={dataTestId}
      className="luxury-card p-6 sm:p-8 bg-gradient-to-br from-[#FEF8EC] via-white to-[#FEF3EC] border-l-4 border-[#E07A3A]"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="bg-[#E07A3A]/10 p-3 rounded-full">
          <Activity className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div className="flex-1">
          <span className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-medium">Pulse Premium · Email-Only</span>
          <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mt-1">
            The Weekly Top 10 — straight to your inbox
          </h3>
        </div>
      </div>

      <p className="text-slate-600 text-sm mb-4 leading-relaxed">
        Every Monday we email you the 10 highest-Pulse properties in {country}. We score on
        <strong> price growth, rental yield, liquidity & RERA/EPC compliance</strong> so you only see listings worth chasing — no fluff, no filler.
      </p>

      <ul className="text-slate-700 text-sm space-y-1 mb-6">
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />Top 10 GREEN-band only — first-class listings curated weekly</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />Hidden-city picks (NRI gold-mines our scrapers find first)</li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />One-click unsubscribe — no commitments</li>
      </ul>

      {isActive ? (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-4 py-3 rounded">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-medium">Active — next digest ships Monday. Renews {new Date(sub.expires_at).toLocaleDateString()}.</span>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-7 py-5 font-semibold"
            data-testid="buyer-pulse-subscribe-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            Subscribe — {price}
          </Button>
          {!authed && (
            <span className="text-xs text-slate-500">We&apos;ll auto-prompt sign-in only at this step — your subscribe action is saved.</span>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-4">
        Powered by Shopperden Pulse Engine v1. Pulse is informational only — always seek independent professional advice before purchase.
      </p>
    </div>
  );
};

export default BuyerPulseSubscribeCard;
